import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Clock, Save, RefreshCw, Trash2, ChevronRight, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { qtyLabel } from '../lib/cartUnits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const { fetchCart } = useCart();
    const [orders, setOrders] = useState([]);
    const [savedCarts, setSavedCarts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingCart, setSavingCart] = useState(false);
    const [cartName, setCartName] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/login');
            return;
        }

        if (isAuthenticated) {
            fetchData();
        }
    }, [isAuthenticated, authLoading, navigate]);

    const fetchData = async () => {
        try {
            const [ordersRes, savedCartsRes] = await Promise.all([
                axios.get(`${API}/orders`, { withCredentials: true }),
                axios.get(`${API}/saved-carts`, { withCredentials: true })
            ]);
            setOrders(ordersRes.data);
            setSavedCarts(savedCartsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReorder = async (orderId) => {
        try {
            await axios.post(`${API}/orders/${orderId}/reorder`, {}, { withCredentials: true });
            await fetchCart();
            toast.success('Items added to cart');
            navigate('/cart');
        } catch (error) {
            toast.error('Failed to reorder');
        }
    };

    const canCancelOrder = (order) => {
        if (order.status !== 'pending' || !order.submitted_at) return false;
        const now = Date.now();
        const submitted = new Date(order.submitted_at).getTime();
        if (Number.isNaN(submitted)) return false;
        if (now > submitted + 24 * 3600 * 1000) return false;
        if (order.cutoff_at && now > new Date(order.cutoff_at).getTime()) return false;
        return true;
    };

    const handleCancelOrder = async () => {
        if (!cancelTarget) return;
        setCancelling(true);
        try {
            await axios.post(`${API}/orders/${cancelTarget}/cancel`, {}, { withCredentials: true });
            toast.success('Order cancelled');
            setCancelTarget(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not cancel order');
        } finally {
            setCancelling(false);
        }
    };

    const handleSaveCart = async () => {
        setSavingCart(true);
        try {
            await axios.post(
                `${API}/saved-carts`,
                { name: cartName || undefined },
                { withCredentials: true }
            );
            toast.success('Cart saved');
            setDialogOpen(false);
            setCartName('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save cart');
        } finally {
            setSavingCart(false);
        }
    };

    const handleLoadSavedCart = async (savedCartId) => {
        try {
            await axios.post(`${API}/saved-carts/${savedCartId}/load`, {}, { withCredentials: true });
            await fetchCart();
            toast.success('Cart loaded');
            navigate('/cart');
        } catch (error) {
            toast.error('Failed to load cart');
        }
    };

    const handleDeleteSavedCart = async (savedCartId) => {
        try {
            await axios.delete(`${API}/saved-carts/${savedCartId}`, { withCredentials: true });
            toast.success('Saved cart deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete cart');
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-8" data-testid="dashboard-page">
            <div className="container-main py-6 md:py-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
                        </h1>
                        <p className="text-slate-500">Manage your orders and saved carts</p>
                    </div>
                    <div className="flex gap-3">
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="gap-2" data-testid="save-cart-btn">
                                    <Save className="w-4 h-4" />
                                    Save Current Cart
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Save Cart</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                    <div>
                                        <Input
                                            placeholder="Cart name (optional)"
                                            value={cartName}
                                            onChange={(e) => setCartName(e.target.value)}
                                            data-testid="cart-name-input"
                                        />
                                    </div>
                                    <Button 
                                        onClick={handleSaveCart}
                                        disabled={savingCart}
                                        className="btn-primary w-full"
                                        data-testid="confirm-save-cart-btn"
                                    >
                                        {savingCart ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Cart'}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Link to="/shop">
                            <Button className="btn-primary gap-2">
                                <ShoppingCart className="w-4 h-4" />
                                Shop Now
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs defaultValue="orders" className="space-y-6">
                    <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
                        <TabsTrigger value="orders" className="gap-2" data-testid="orders-tab">
                            <Package className="w-4 h-4" />
                            Orders
                        </TabsTrigger>
                        <TabsTrigger value="saved" className="gap-2" data-testid="saved-tab">
                            <Save className="w-4 h-4" />
                            Saved Carts
                        </TabsTrigger>
                    </TabsList>

                    {/* Orders Tab */}
                    <TabsContent value="orders" className="space-y-4">
                        {orders.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
                                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No orders yet</h3>
                                <p className="text-slate-500 mb-4">Start shopping to see your orders here</p>
                                <Link to="/shop">
                                    <Button className="btn-primary">Shop Now</Button>
                                </Link>
                            </div>
                        ) : (
                            orders.map((order) => (
                                <div 
                                    key={order.order_id} 
                                    className="order-card"
                                    data-testid={`order-${order.order_id}`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-semibold text-slate-900">
                                                    Order #{order.order_id.slice(-8)}
                                                </h3>
                                                <Badge 
                                                    className={
                                                        order.payment_status === 'paid' 
                                                            ? 'bg-green-100 text-green-700'
                                                            : order.status === 'pending'
                                                                ? 'bg-yellow-100 text-yellow-700'
                                                                : 'bg-slate-100 text-slate-700'
                                                    }
                                                >
                                                    {order.payment_status === 'paid' ? 'Paid' : order.status}
                                                </Badge>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    {new Date(order.created_at).toLocaleDateString()}
                                                </span>
                                                <span>{order.items.length} items</span>
                                                {order.delivery_info?.fulfil_label && (
                                                    <span className="text-slate-600" data-testid={`order-fulfil-${order.order_id}`}>
                                                        {order.delivery_type === 'delivery' ? 'Delivery' : 'Pickup'}: {order.delivery_info.fulfil_label}
                                                        {order.delivery_info.time_slot_label ? ` · ${order.delivery_info.time_slot_label}` : ''}
                                                        {order.delivery_type === 'pickup' && order.delivery_info.window_label ? ` · ${order.delivery_info.window_label}` : ''}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col sm:items-end gap-2">
                                            <Button 
                                                onClick={() => handleReorder(order.order_id)}
                                                className="btn-secondary gap-2"
                                                data-testid={`reorder-${order.order_id}`}
                                            >
                                                <RefreshCw className="w-4 h-4" />
                                                Reorder
                                            </Button>
                                            {canCancelOrder(order) && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setCancelTarget(order.order_id)}
                                                    className="gap-2 border-red-200 text-red-600 hover:bg-red-50"
                                                    data-testid={`cancel-order-${order.order_id}`}
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                    Cancel
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Order Items Preview */}
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <div className="flex flex-wrap gap-2">
                                            {order.items.slice(0, 5).map((item) => (
                                                <span 
                                                    key={item.product_id}
                                                    className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded"
                                                >
                                                    {qtyLabel(item)} {item.name}
                                                </span>
                                            ))}
                                            {order.items.length > 5 && (
                                                <span className="text-xs text-slate-400">
                                                    +{order.items.length - 5} more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </TabsContent>

                    {/* Saved Carts Tab */}
                    <TabsContent value="saved" className="space-y-4">
                        {savedCarts.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
                                <Save className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No saved carts</h3>
                                <p className="text-slate-500">Save your cart to quickly reorder your favorites</p>
                            </div>
                        ) : (
                            savedCarts.map((cart) => (
                                <div 
                                    key={cart.saved_cart_id} 
                                    className="order-card"
                                    data-testid={`saved-cart-${cart.saved_cart_id}`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div>
                                            <h3 className="font-semibold text-slate-900 mb-1">
                                                {cart.name}
                                            </h3>
                                            <div className="flex items-center gap-4 text-sm text-slate-500">
                                                <span>{cart.items.length} items</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDeleteSavedCart(cart.saved_cart_id)}
                                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                data-testid={`delete-cart-${cart.saved_cart_id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                onClick={() => handleLoadSavedCart(cart.saved_cart_id)}
                                                className="btn-secondary gap-2"
                                                data-testid={`load-cart-${cart.saved_cart_id}`}
                                            >
                                                Load Cart
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </TabsContent>
                </Tabs>

                <Dialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) setCancelTarget(null); }}>
                    <DialogContent data-testid="cancel-order-dialog">
                        <DialogHeader>
                            <DialogTitle>Cancel this order?</DialogTitle>
                            <DialogDescription className="text-sm text-slate-600">
                                This will cancel your order. You can only cancel while it's still pending, within 24 hours of placing it and before the cut-off. This can't be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex gap-2 justify-end mt-4">
                            <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="cancel-dialog-keep">
                                Keep order
                            </Button>
                            <Button
                                onClick={handleCancelOrder}
                                disabled={cancelling}
                                className="bg-red-600 text-white hover:bg-red-700 gap-2"
                                data-testid="cancel-dialog-confirm"
                            >
                                {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                                Yes, cancel order
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
